import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './OnePager.module.scss';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import DealSummaryEditorPlate from './editor/DealSummaryEditorPlate';
import { generateDocumentFromTemplate, getDocument, saveDocument } from '../../store/investor/investorApiHelper';
import { closeOnePager, setOnePagerData } from '../../store/investor/action';
import { Button, Modal } from '../_custom';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';
import { unwrapDocVersion, runMigrations } from '../PlateEditor/migrations';
import { useCommitDiffGate } from '../hooks/useCommitDiffGate';

const ONE_PAGER_DOCUMENT_TYPE = 'ONE_PAGER';
// Display name on the unified document row (matches the Reports template label).
const ONE_PAGER_DOC_NAME = 'Detailed One Pager';

interface OnePagerProps {
    companyName?: string;
    metadataUpdate?: (fileInfo: { url?: string; fileName?: string; docId?: string | number }) => void;
    categoryId?: number;
    flowId?: number;
}

function OnePager({ companyName, metadataUpdate, categoryId, flowId }: OnePagerProps) {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const onePagerState = useAppSelector((state) => state.investor.onePager);
    const assesseeOrgId = onePagerState.assesseeOrgId;
    const source = onePagerState.source;
    const resolvedCompanyName = companyName || (onePagerState as any).companyName || '';
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);

    // The lineage id of this org's One Pager document. Latched from the loaded
    // entry (or from the id the backend mints on the first save) and forwarded on
    // every save so versions group under one lineage. Regenerate NEVER re-mints —
    // a lineage is created only when the org's first One Pager comes into being.
    const genIdRef = useRef<string | undefined>(undefined);
    // Save commits a NEW version only when content differs from the last
    // committed baseline — otherwise Save is a no-op close, so version history
    // never accretes identical entries. Unlatched = nothing committed yet
    // (fresh doc / loaded draft), so the first commit always persists.
    const commitGate = useCommitDiffGate();
    // Serializes EVERY saveDocument call (autosave drafts, commits, history
    // pre-flight/restore). Without it, an autosave firing concurrently with a
    // commit on a brand-new doc (no genId latched yet) both send no genId and
    // the backend mints two separate lineages — a duplicate report. Payloads
    // are built inside the queued thunk so the second save reads the genId the
    // first one latched.
    const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
    const enqueueSave = <T,>(run: () => Promise<T>): Promise<T> => {
        const next = saveChainRef.current.then(run, run);
        saveChainRef.current = next.catch(() => undefined);
        return next;
    };

    useEffect(() => {
        return () => {
            dispatch(closeOnePager());
        };
    }, []);

    useEffect(() => {
        fetchOnePagerTemplateData();
    }, [assesseeOrgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    // Generates a fresh One Pager via the composer service when no saved doc exists
    // (or on regenerate). The compose itself persists nothing — the first autosave
    // or Save after it creates the document (reusing the latched genId, if any).
    const fetchDataForOnePager = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        dispatch(setOnePagerData({ loading: true }));
        try {
            const result = await generateDocumentFromTemplate({
                assesseeOrgId: assesseeOrgId as string,
                // investorOrgId (the PNO) is sent as the x-organization-id header so Polus
                // resolves the ONE_PAGER ViewConfig for this PNO. Without it the compose
                // falls back to the DEFAULT config and fails (_composeFailed).
                investorOrgId,
                documentType: ONE_PAGER_DOCUMENT_TYPE,
            });
            const value = result.document ?? [{ type: 'p', children: [{ text: '' }] }];
            setEditorInitialData(sanitizeSlateValue(value));
            setTimeout(() => {
                setEditorReady(true);
                dispatch(setOnePagerData({ loading: false }));
            }, 200);
        } catch (err) {
            console.error('One Pager generation error:', err);
            toast.error('Could not generate the One Pager. Please try again.');
            dispatch(setOnePagerData({ loading: false }));
            // Regenerating an already-open doc: keep the drawer open with its existing
            // content rather than closing it out from under the user's edits. On the
            // initial fetch there is no existing content to preserve, so close instead
            // of leaving editorReady=false forever (a permanently blank drawer).
            if (isRegenerate) {
                setEditorReady(true);
            } else {
                dispatch(closeOnePager());
            }
        }
    };

    // Unified load: fetch the org's newest live ONE_PAGER (autosave draft
    // preferred — even a never-committed one — else the latest committed
    // version; same open behaviour the legacy endpoint had). No saved doc →
    // compose a fresh one.
    const fetchOnePagerTemplateData = async () => {
        setEditorReady(false);
        genIdRef.current = undefined;
        commitGate.reset();
        dispatch(setOnePagerData({ loading: true }));
        try {
            const doc = await getDocument({
                assesseeOrgId: assesseeOrgId as string,
                documentType: ONE_PAGER_DOCUMENT_TYPE,
            });
            const { version, value } = unwrapDocVersion(doc?.content ?? []);
            if (value?.length) {
                genIdRef.current = doc?.genId;
                // A loaded DRAFT means uncommitted changes exist — leave the commit
                // baseline unlatched so the next Save commits them as a version.
                if (doc?.status === 'READY') commitGate.latch(value);
                const migrated = runMigrations(version, value);
                const sanitizedData = sanitizeSlateValue(migrated);
                setEditorInitialData(sanitizedData);
                setUserInfo({
                    userName: doc?.lastUpdatedBy,
                    onePagerData: { lastUpdatedAt: doc?.lastUpdatedAt },
                });
                setTimeout(() => {
                    setEditorReady(true);
                    dispatch(setOnePagerData({ loading: false }));
                }, 200);
            } else fetchDataForOnePager();
        } catch (err) {
            console.error('One Pager load error:', err);
            // Load failed — do NOT fall through to a fresh compose, which would be
            // committable over the existing doc after a transient failure. Close.
            toast.error('Could not load the One Pager. Please try again.');
            dispatch(setOnePagerData({ loading: false }));
            dispatch(closeOnePager());
        }
    };

    // Single save path for the unified document engine. `isDraft = true` →
    // autosave: upserts the lineage's single live draft (status DRAFT), never
    // closes the drawer, never versions. `isDraft = false` → commit: creates a
    // new committed version (promoting the live draft when one exists) — but
    // ONLY when the content actually differs from the last committed version.
    const buildSavePayload = (editorContent: any[], isDraft: boolean) => ({
        assesseeOrgId: assesseeOrgId as string,
        investorOrgId,
        createdBy: emailId as string,
        ...(genIdRef.current ? { genId: genIdRef.current } : {}),
        documentType: ONE_PAGER_DOCUMENT_TYPE,
        name: ONE_PAGER_DOC_NAME,
        editor: 'plate',
        formatType: 'DOC',
        status: isDraft ? 'DRAFT' : 'READY',
        content: { version: 1, editor: 'plate', value: editorContent },
    });

    const latchGenId = (saved: any) => {
        if (saved?.genId && !genIdRef.current) genIdRef.current = saved.genId;
    };

    const saveOnePagerTemplateDataForOrg = (editorContent, keepDrawerOpen = false, onSuccess, isDraft = false) => {
        if (!isDraft && savingData) return;
        // Commit with no diff since the last committed version: skip the server
        // round-trip entirely — no new version for unchanged content.
        if (!isDraft && commitGate.isUnchanged(editorContent)) {
            if (!keepDrawerOpen) dispatch(closeOnePager());
            onSuccess?.();
            return;
        }
        if (!isDraft) setSavingData(true);
        enqueueSave(() => saveDocument(buildSavePayload(editorContent || [], isDraft)))
            .then((saved) => {
                latchGenId(saved);
                if (isDraft) {
                    onSuccess?.();
                    return;
                }
                commitGate.latch(editorContent || []);
                setTimeout(() => {
                    if (!keepDrawerOpen) {
                        dispatch(closeOnePager());
                    }
                    setSavingData(false);
                    onSuccess?.();
                }, 1000);
            })
            .catch((err) => {
                console.error('One Pager save error:', err);
                toast.error('Could not save the One Pager. Please try again.');
                if (!isDraft) setSavingData(false);
            });
    };

    // Direct commit for the history flows (pre-flight save + restore). Bypasses
    // the savingData re-entrancy guard — a dropped concurrent save would hang the
    // restore's reload — and always versions (restore is explicit user intent).
    // Still serialized through the save chain so it can't race an autosave into
    // minting a second lineage.
    const persistCommittedVersion = async (editorContent: any[]) => {
        const saved = await enqueueSave(() => saveDocument(buildSavePayload(editorContent, false)));
        latchGenId(saved);
        commitGate.latch(editorContent);
    };

    // V2-only: the legacy Slate-based DealSummaryEditor was removed. The
    // `editor.onePager.v2` key is retained in visibilityKeys.ts for reference/revert
    // but is no longer read here.
    const EditorComponent: any = DealSummaryEditorPlate;

    return (
        <div className={styles.OnePager}>
            {(source === 'CREATE_DEAL' ? onePagerState.open : editorReady) && (
                <EditorComponent
                    openModal={source === 'CREATE_DEAL' ? true : editorReady}
                    closeModal={() => dispatch(closeOnePager())}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    generating={onePagerState.loading}
                    companyNameProp={resolvedCompanyName}
                    userInfo={userInfo}
                    assesseeOrgId={assesseeOrgId ?? undefined}
                    saveOnePagerTemplateDataForOrg={saveOnePagerTemplateDataForOrg}
                    persistCommittedVersion={persistCommittedVersion}
                    getDocumentGenId={() => genIdRef.current}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    source={source}
                    categoryId={categoryId}
                    flowId={flowId}
                    metadataUpdate={metadataUpdate}
                    savingData={savingData}
                />
            )}

            {showRegenConfirmModal && (
                <Modal
                    open={showRegenConfirmModal}
                    onClose={() => setShowRegenConfirmModal(false)}
                    size="flexible"
                    className={styles.RegenModalOverDrawer}
                >
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate One Pager</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate One pager? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                }}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForOnePager(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default OnePager;
